import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Draggable, DraggableProvided, DraggableStateSnapshot, Droppable } from 'react-beautiful-dnd';
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import { TFunction } from 'i18next';
import { Box, SiteVariablesPrepared } from '@fluentui/react-northstar';

import {
  BoardItem,
  IBoardItem,
  IBoardItemCardLayout,
  IPreparedBoardItem,
} from './BoardItem';
import type { TPlaceholderPosition } from './BoardLane';
import { SkeletonBoardCard } from '../../../components/shared/components/Skeleton';
import { holdGrabbingCursor } from '../../../components/shared/utils/grabbingCursor';

const ESTIMATED_ROW_HEIGHT = 240;
const OVERSCAN = 4;
const LOAD_MORE_SKELETONS = 2;
const DROP_MARGIN_Y = 8;

type LaneVirtualizer = Virtualizer<HTMLDivElement, Element>;

export interface VirtualLaneBodyProps {
  laneKey: string;
  preparedItems: IPreparedBoardItem[];
  isAnalystAccess: boolean;
  isSlaEnabled: boolean;
  boardItemCardLayout: IBoardItemCardLayout;
  t: TFunction<'translation', undefined>;
  editItemDialog?: (boardItem: IBoardItem) => JSX.Element;
  isLaneLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  placeholderPosition?: TPlaceholderPosition;
  placeholderTargetIndex?: number | null;
}

const DropPlaceholder = ({ top, height }: { top: number; height: number }) => (
  <Box
    variables={({ colorScheme }: SiteVariablesPrepared) => ({
      backgroundColor: colorScheme.brand.background1,
      borderColor: colorScheme.brand.foreground3,
    })}
    styles={{
      left: '1.25rem',
      right: '1.25rem',
      top: top + DROP_MARGIN_Y + 'px',
      height: Math.max(height - DROP_MARGIN_Y * 2, 0) + 'px',
      position: 'absolute',
      borderRadius: '4px',
      borderWidth: '1px',
      zIndex: 0,
    }}
  />
);

const PlaceholderSync = ({
  active,
  onChange,
}: {
  active: boolean;
  onChange: (active: boolean) => void;
}) => {
  useEffect(() => {
    onChange(active);
  }, [active, onChange]);
  return null;
};

const DraggableRow = (props: {
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  virtualizer: LaneVirtualizer;
  index: number;
  start: number;
  /** The closed hand from the press, as the column grips do - react-beautiful-dnd only closes it once the card moves. */
  canDrag: boolean;
  children: React.ReactNode;
}) => {
  const { provided, snapshot, virtualizer, index, start, canDrag, children } = props;
  const innerRef = provided.innerRef;

  const setRef = useCallback(
    (el: HTMLElement | null) => {
      innerRef(el);
      if (el && !snapshot.isDragging) virtualizer.measureElement(el);
    },
    [innerRef, virtualizer, snapshot.isDragging]
  );

  return (
    <div
      ref={setRef}
      data-index={index}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      onPointerDown={e => { if (canDrag && e.button === 0) holdGrabbingCursor(); }}
      style={{
        position: 'absolute',
        top: start,
        left: 0,
        width: '100%',
        ...provided.draggableProps.style,
      }}
    >
      {children}
    </div>
  );
};

export const VirtualLaneBody = (props: VirtualLaneBodyProps) => {
  const {
    laneKey,
    preparedItems,
    isAnalystAccess,
    isSlaEnabled,
    boardItemCardLayout,
    t,
    editItemDialog,
    isLaneLoading,
    hasMore,
    onLoadMore,
  } = props;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [placeholderActive, setPlaceholderActive] = useState(false);

  const placeholderIndex = placeholderActive ? preparedItems.length : null;
  const skeletonRows = isLaneLoading ? LOAD_MORE_SKELETONS : 0;

  const virtualizer = useVirtualizer({
    count: preparedItems.length + (placeholderActive ? 1 : 0) + skeletonRows,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
    getItemKey: (index) => preparedItems[index]?.itemKey ?? `__row_${index}`,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const scrollOffset = virtualizer.scrollOffset ?? 0;

  const dropIndex = props.placeholderTargetIndex;
  const showDrop = placeholderActive && dropIndex != null;
  let dropTop = 0;
  let dropHeight = 0;
  if (showDrop) {
    const match = virtualItems.find((r) => r.index === dropIndex);
    if (match) {
      dropTop = match.start;
    } else if (dropIndex <= 0) {
      dropTop = 0;
    } else {
      const realRows = virtualItems.filter((r) => r.index < preparedItems.length);
      const lastReal = realRows.length ? realRows[realRows.length - 1] : null;
      dropTop = lastReal ? lastReal.start + lastReal.size : dropIndex * ESTIMATED_ROW_HEIGHT;
    }
    const pos = props.placeholderPosition;
    dropHeight = pos?.[3] ?? match?.size ?? ESTIMATED_ROW_HEIGHT - 16;
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !hasMore || isLaneLoading || placeholderActive) return;
    // Only after the user has actually scrolled down. Guards against firing on mount, when
    // the virtualizer hasn't measured yet and scrollHeight collapses to clientHeight.
    if (el.scrollTop <= 0) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= ESTIMATED_ROW_HEIGHT) {
      onLoadMore?.();
    }
  }, [scrollOffset, hasMore, isLaneLoading, placeholderActive, preparedItems.length, onLoadMore]);

  return (
    <Droppable
      droppableId={laneKey}
      mode="virtual"
      isDropDisabled={!isAnalystAccess}
      renderClone={(provided, snapshot, rubric) => {
        const clone = (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={provided.draggableProps.style}
          >
            <BoardItem
              isSlaEnabled={isSlaEnabled}
              isDragging={snapshot.isDragging}
              item={preparedItems[rubric.source.index]}
              t={t}
              boardItemCardLayout={boardItemCardLayout}
            />
          </div>
        );
        return typeof document !== 'undefined'
          ? createPortal(clone, document.body)
          : clone;
      }}
    >
      {(provided, snapshot) => (
        <div
          ref={(el) => {
            scrollRef.current = el;
            provided.innerRef(el);
          }}
          {...provided.droppableProps}
          style={{ height: '100%', width: '100%', overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}
        >
          <PlaceholderSync active={snapshot.isUsingPlaceholder} onChange={setPlaceholderActive} />
          <div style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
            {showDrop && <DropPlaceholder top={dropTop} height={dropHeight} />}
            {virtualItems.map((vRow) => {
              const virtualStyle: React.CSSProperties = {
                position: 'absolute',
                top: vRow.start,
                left: 0,
                width: '100%',
              };

              if (placeholderIndex !== null && vRow.index === placeholderIndex) {
                return (
                  <div
                    key={vRow.key as React.Key}
                    data-index={vRow.index}
                    ref={virtualizer.measureElement}
                    style={virtualStyle}
                  />
                );
              }

              const item = preparedItems[vRow.index];

              if (!item) {
                return (
                  <div
                    key={vRow.key as React.Key}
                    data-index={vRow.index}
                    ref={virtualizer.measureElement}
                    style={virtualStyle}
                  >
                    <div style={{ margin: '0 1.25rem .5rem' }}>
                      <SkeletonBoardCard />
                    </div>
                  </div>
                );
              }

              return (
                <Draggable
                  draggableId={item.itemKey}
                  index={vRow.index}
                  key={item.itemKey}
                  // a card still being created has no ticket behind it to move yet
                  isDragDisabled={!isAnalystAccess || item.title === 'temp'}
                >
                  {(dProvided, dSnapshot) => (
                    <DraggableRow
                      provided={dProvided}
                      snapshot={dSnapshot}
                      virtualizer={virtualizer}
                      index={vRow.index}
                      start={vRow.start}
                      canDrag={isAnalystAccess && item.title !== 'temp'}
                    >
                      <BoardItem
                        isSlaEnabled={isSlaEnabled}
                        isDragging={dSnapshot.isDragging}
                        item={item}
                        t={t}
                        boardItemCardLayout={boardItemCardLayout}
                        {...(editItemDialog && { editItemDialog: editItemDialog(item) })}
                      />
                    </DraggableRow>
                  )}
                </Draggable>
              );
            })}
          </div>
        </div>
      )}
    </Droppable>
  );
};
